import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { DataGridPremium } from '@mui/x-data-grid-premium'
import {
  useCardDeliveryAdress,
  useCardsV2,
  usePatchCardConfig,
  usePatchCardV1,
  usePrivateCustomerCardConfig,
  useReissueCard,
  useResetCardPin,
  useSyncCards,
} from 'api/react-query'
import useMaterialNotification from 'hooks/useMaterialNotification.tsx'
import { isEqual } from 'lodash'
import FigmaBox from 'mynt-components/components/FigmaBox.tsx'
import ImageContainer from 'mynt-components/components/ImageContainer.tsx'
import type React from 'react'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { ImageKeys } from 'tiger/libs/ImageRepository.ts'
import { BackOfficeCardStatusDtoV1, type ListPlasticResponse, type VisaCardResponse } from '../../api/swagger/definitions/backoffice'
import { ModalActivateCard } from '../../components/Modals.tsx'
import { SelectController, SwitchController, TextFieldController } from '../../components/react-hook-components.tsx'

interface CustomerCardTabOverviewSingleCardDetailsPrivateCardProps {
  card?: VisaCardResponse
  plastics?: ListPlasticResponse[]
  customerId: string
  internalCardId: string
  isLoading?: boolean
  personId: string
}

type ClickEvent = React.MouseEvent<HTMLDivElement, MouseEvent>

type ActivateCardProps = {
  customerId: string
  onShow?: () => void
  onClose?: () => void
  showModal: boolean
  plastic: ListPlasticResponse
}

const ActivateCard: React.FC<ActivateCardProps> = (props) => {
  const { showModal, onShow, ...modalProps } = props
  const [activateModal, setActivateModal] = useState(false)
  const anchor = useRef<HTMLDivElement>(null)

  const handleClick = (event: ClickEvent) => {
    event.persist()
    setActivateModal(true)
    onShow?.()
  }

  const handleClose = () => {
    setActivateModal(false)
  }

  return (
    <>
      {activateModal && showModal && <ModalActivateCard {...modalProps} anchor={anchor.current} onClose={handleClose} />}
      <FigmaBox onClick={handleClick} justify="center" align="center" fullWidth>
        {/** biome-ignore lint/a11y/noStaticElementInteractions: <explanation> */}
        {/** biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
        <div onClick={() => null} />

        <FigmaBox ref={anchor}>
          <ImageContainer imageKey={ImageKeys.activateCardIcon} />
        </FigmaBox>
      </FigmaBox>
    </>
  )
}

type FormValues = {
  status: BackOfficeCardStatusDtoV1 | undefined
  limit: string | undefined
  allowAtm: boolean
}

const CustomerCardTabOverviewSingleCardDetailsPrivateCard: React.FC<CustomerCardTabOverviewSingleCardDetailsPrivateCardProps> = ({
  card,
  plastics,
  customerId,
  internalCardId,
  isLoading,
  personId,
}) => {
  const [loaders, _setLoaders] = useState({
    status: false,
    limit: false,
    atm: false,
    sync: false,
    resetLimit: false,
    reissue: false,
    resetPinAttempts: false,
  })

  const setLoader = (loader: keyof typeof loaders, value = true) => _setLoaders((loaders) => ({ ...loaders, [loader]: value }))

  const [currentCardActivate, setCurrentCardActivate] = useState<string>()
  const cardId = card?.id || ''

  const cardMutation = usePatchCardV1(customerId, internalCardId)
  const cardConfigMutation = usePatchCardConfig(customerId, cardId)
  const reissueCardMutation = useReissueCard(customerId)
  const resetCardPainMutation = useResetCardPin(customerId)
  const syncMutation = useSyncCards(customerId)

  const notify = useMaterialNotification()

  const { data: cardsV2 } = useCardsV2(customerId)
  const cardV2 = cardsV2?.find((c: { id: string }) => c.id === internalCardId)

  const { data: cardConfig } = usePrivateCustomerCardConfig(customerId, cardId)
  const { data: cardDeliveryData } = useCardDeliveryAdress(customerId, personId)

  const form = useForm({
    values: {
      status: cardV2?.status ?? ('' as BackOfficeCardStatusDtoV1),
      limit: String(cardConfig?.monthlyLimit ?? ''),
      allowAtm: Boolean(cardConfig?.allowAtm),
    },
    mode: 'onBlur',
  })

  const handleCardConfigSubmit = (loader: keyof typeof loaders) => async (data: FormValues) => {
    const payload = {
      monthlyLimit: data.limit === '' ? null : Number(data.limit),
      allowAtm: data.allowAtm,
    }

    const dataIsEqual = isEqual(payload, {
      monthlyLimit: cardConfig?.monthlyLimit,
      allowAtm: cardConfig?.allowAtm,
    })

    if (dataIsEqual) return

    setLoader(loader)

    await cardConfigMutation.mutateAsync(payload).finally(() => {
      setLoader(loader, false)
    })

    notify(`Updated card config`)
  }

  const handleOnSubmit = (loader: keyof typeof loaders) => async (data: FormValues) => {
    if (!data.status || data.status === cardV2?.status) return

    const status = data.status as BackOfficeCardStatusDtoV1

    setLoader(loader)

    await cardMutation
      .mutateAsync({
        status,
      })
      .finally(() => {
        setLoader(loader, false)
      })

    notify(`Updated card status`)
  }

  const handleResetLimit = async () => {
    setLoader('resetLimit')

    await cardConfigMutation
      .mutateAsync({
        monthlyLimitActive: false,
      })
      .finally(() => {
        setLoader('resetLimit', false)
      })

    notify(`Card limit has been reset`)
  }

  const handleReissueCard = async () => {
    setLoader('reissue')

    await reissueCardMutation.mutateAsync(cardId).finally(() => {
      setLoader('reissue', false)
    })

    notify(`Card has been reissued`)
  }

  const handleResetPin = async () => {
    setLoader('resetPinAttempts')

    await resetCardPainMutation.mutateAsync(cardId).finally(() => {
      setLoader('resetPinAttempts', false)
    })

    notify(`Card PIN attempts have been reset`)
  }

  const notifyAfterPromise = (message: string) => () => notify(message)

  const loadingAdornment = isLoading ? (
    <Box sx={{ paddingRight: '16px' }}>
      <CircularProgress size="20px" />
    </Box>
  ) : null

  const statusPatchOptions = Object.values(BackOfficeCardStatusDtoV1).map((status) => ({ label: status, value: status }))

  return (
    <Box sx={{ display: 'flex', gap: '32px', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', gap: '16px', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <SelectController
            disabled={loaders.status || isLoading}
            onChange={() => form.handleSubmit(handleOnSubmit('status'))()}
            control={form.control}
            name="status"
            labelText="Status"
            options={statusPatchOptions}
          />
          <TextFieldController
            disabled={loaders.limit || isLoading}
            onBlur={form.handleSubmit(handleCardConfigSubmit('limit'))}
            control={form.control}
            name="limit"
            labelText="Limit"
            InputProps={{ endAdornment: loadingAdornment }}
          />
          <SwitchController
            disabled={cardConfigMutation.isLoading || isLoading}
            onBlur={form.handleSubmit(handleCardConfigSubmit('atm'))}
            control={form.control}
            name="allowAtm"
            labelText={<Box sx={{ paddingLeft: '8px' }}>ATM</Box>}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Button
            variant="secondary"
            onClick={() => syncMutation.mutateAsync(card?.id as string).then(notifyAfterPromise('Synced card'))}
            loading={syncMutation.isLoading}
          >
            Sync
          </Button>
          <Button variant="secondary" onClick={handleResetLimit} loading={loaders.resetLimit}>
            Reset limit
          </Button>
          <Button variant="secondary" onClick={handleReissueCard} loading={loaders.reissue}>
            Reissue
          </Button>
          <Button variant="secondary" onClick={handleResetPin} loading={loaders.resetPinAttempts}>
            Reset PIN attempts
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
            }}
          >
            Card delivery address
          </Typography>
          {card?.cardAddress?.address1 && <Typography variant="subtitle1">{card?.cardAddress?.address1}</Typography>}
          {card?.cardAddress?.address2 && <Typography variant="subtitle1">{card?.cardAddress?.address2}</Typography>}
          {card?.cardAddress?.city && <Typography variant="subtitle1">{card?.cardAddress?.city}</Typography>}
          {/* @ts-expect-error Incorrect swagger type */}
          {card?.cardAddress?.zipCode && <Typography variant="subtitle1">{card?.cardAddress?.zipCode}</Typography>}
          {card?.cardAddress?.country && <Typography variant="subtitle1">{card?.cardAddress?.country}</Typography>}
        </Box>
        {card && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '32px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Typography variant="body2">Card number: {card.maskedCardNumber}</Typography>
              <Typography variant="body2">
                Expiration: {card.expiration?.year}/{card.expiration?.month}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Typography variant="body2">Card role: {card.cardRole}</Typography>
              <Typography variant="body2">Reason: {card.reason}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Typography variant="body2">Product code: {card.productCode}</Typography>
              <Typography variant="body2">Company name: {card.embossing?.companyName}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Typography variant="body2">Enfuce card status: {card.status}</Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Box>
        <Typography variant="h2">Plastics</Typography>
        <Box sx={{ height: isLoading ? 200 : 'auto', paddingTop: '16px' }}>
          <DataGridPremium
            loading={isLoading}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnFilter
            disableColumnPinning
            disableColumnReorder
            disableColumnResize
            rows={plastics || []}
            slots={{
              noRowsOverlay: () => (
                <Stack
                  sx={{
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  No plastics found
                </Stack>
              ),
              loadingOverlay: () => <LinearProgress />,
            }}
            columns={[
              {
                field: 'firstName',
                headerName: 'First name',
                flex: 1,
                sortable: false,
              },
              {
                field: 'lastName',
                headerName: 'Last name',
                flex: 1,
                sortable: false,
              },
              {
                field: 'companyName',
                headerName: 'Company',
                flex: 1,
                sortable: false,
              },
              {
                field: 'createdDate',
                headerName: 'Created date',
                flex: 1,
                sortable: false,
              },
              {
                field: 'productionDate',
                headerName: 'Production date',
                flex: 1.25,
                sortable: false,
              },
              {
                field: 'productionType',
                headerName: 'Production type',
                flex: 1.25,
                sortable: false,
              },
              {
                field: 'expiration',
                headerName: 'Expiration',
                valueGetter: (_value, row: any) => `${row.expiration.month}/${row.expiration.year}`,
                flex: 1,
                sortable: false,
              },
              {
                field: 'status',
                headerName: 'Status',
                flex: 1,
                sortable: false,
              },
              {
                field: 'activateCard',
                headerName: 'Activate Card',
                flex: 1.25,
                sortable: false,
                renderCell: (params) => (
                  <ActivateCard
                    customerId={customerId}
                    onShow={() => setCurrentCardActivate(params.row.id)}
                    showModal={params.row.id === currentCardActivate}
                    onClose={() => setCurrentCardActivate(undefined)}
                    plastic={params.row}
                  />
                ),
              },
            ]}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default CustomerCardTabOverviewSingleCardDetailsPrivateCard
